import { useEffect } from 'react'
import { auth, onIdTokenChanged, setAuthTenant } from '../config/firebase.config'
import { client } from '@hapana/shared/web/services/requests.service'
import useAuthStore from '../stores/auth.store'
import { extractSubdomain } from '../services/tenant.service'
import { getAnalyticsClient } from '@hapana/shared/analytics/src'
import { useTenantIds } from './use-tenant-config'

const MINIMUM_TENANT_ID_LENGTH = 10

function getIsDemoContext() {
  if (typeof window === 'undefined') return false
  const hostname = window.location.hostname
  if (hostname.split('.')[0] === 'demo') return true
  if (
    (hostname === 'localhost' || hostname === '127.0.0.1') &&
    import.meta.env.VITE_CORE_SHARED_STAFF_TENANT_ID
  )
    return true
  return false
}

/**
 * Firebase Auth Initialization Hook
 *
 * Manages Firebase authentication state and token refresh automatically using onIdTokenChanged.
 * Sets up GraphQL client headers with auth tokens and tenant information.
 * Handles tenant configuration for multi-tenant environments.
 * Prevents component remounting on login page by managing loading states appropriately.
 * Provides development debug helpers for token inspection in dev environment.
 */
export function useAuthInitialize() {
  const { staffTenantId, isLoading: tenantLoading, error: tenantError } = useTenantIds()
  const { user, loading: authLoading } = useAuthStore()
  useEffect(() => {
    let isMounted = true

    if (typeof window !== 'undefined') {
      window.__firebaseAuth = auth
    }

    // Set Firebase auth on the GraphQL client for token refresh
    client.setFirebaseAuth(auth)

    useAuthStore.setState({ loading: true, initialized: false })

    const unsubscribe = onIdTokenChanged(auth, async (firebaseUser) => {
      if (!isMounted) {
        return
      }

      if (!firebaseUser) {
        useAuthStore.setState({
          user: null,
          idToken: null,
          loading: false,
          initialized: true,
          headersReady: true,
        })
        client.setHeader('Authorization', null)
        client.setHeader('x-auth-mechanism', null)
        client.setHeader('x-tenant', extractSubdomain())
        return
      }

      try {
        const subdomain = extractSubdomain()

        // Always use getIdToken(false) here — returns the latest cached token.
        // Forcing a refresh here would cause an infinite loop
        // (getIdToken(true) → onIdTokenChanged → repeat).
        const token = await firebaseUser.getIdToken(false)

        if (!isMounted) {
          return
        }

        client.setHeader('Authorization', `Bearer ${token}`)
        client.setHeader('x-auth-mechanism', 'core')
        client.setHeader('x-tenant', subdomain)

        useAuthStore.setState({
          user: firebaseUser,
          idToken: token,
          loading: false,
          headersReady: true,
          initialized: true,
        })

        try {
          getAnalyticsClient()?.identify(firebaseUser.uid, {
            email: firebaseUser.email,
            name: firebaseUser.displayName,
          })
        } catch (_analyticsError) {
          // Non-critical
        }
      } catch (_) {
        if (!isMounted) {
          return
        }

        useAuthStore.setState({
          user: null,
          idToken: null,
          loading: false,
          initialized: true,
          headersReady: false,
        })
      }
    })

    return () => {
      isMounted = false
      unsubscribe()
    }
  }, [])

  useEffect(() => {
    const isOnLoginPage =
      typeof window !== 'undefined' &&
      (window.location.pathname === '/login' || window.location.pathname.includes('/login'))

    // /magic-link and /reset-password are self-managing routes — Firebase redirects here without
    // brand context. Each component redirects to the brand subdomain before consuming the link.
    // Skip tenant initialization entirely for both routes.
    const isSelfManagingRoute =
      typeof window !== 'undefined' &&
      (window.location.pathname === '/magic-link' ||
        window.location.pathname.startsWith('/magic-link/') ||
        window.location.pathname === '/reset-password' ||
        window.location.pathname.startsWith('/reset-password/'))
    if (isSelfManagingRoute) {
      useAuthStore.setState({ tenantError: null, initialized: true })
      return
    }

    // Demo public routes (verify, creating, signup) manage their own tenant.
    // Do NOT override auth.tenantId here — the demo flow sets it to VITE_CORE_SHARED_STAFF_TENANT_ID
    // which may differ from the tenant resolved via gateway for the 'demo' subdomain.
    const DEMO_AUTH_PATHS = ['/verify', '/creating', '/signup']
    const isDemoAuthRoute =
      typeof window !== 'undefined' &&
      DEMO_AUTH_PATHS.some(
        (p) => window.location.pathname === p || window.location.pathname.startsWith(p + '/')
      )
    if (getIsDemoContext() && isDemoAuthRoute) {
      // Let the demo page components control the tenant — just mark initialized
      useAuthStore.setState({ tenantError: null, initialized: true })
      return
    }

    if (staffTenantId && staffTenantId.length > MINIMUM_TENANT_ID_LENGTH) {
      setAuthTenant(staffTenantId)
      // Don't set initialized here - let the auth state change handler do it
      useAuthStore.setState({
        tenantError: null,
      })
    } else if (tenantError) {
      if (isOnLoginPage) {
        // Only mark as initialized on login page to allow showing the form
        useAuthStore.setState({
          initialized: true,
          tenantError: null,
        })
      } else {
        useAuthStore.setState({
          tenantError: tenantError.message || 'Failed to load tenant configuration',
        })
      }
    } else if (!tenantLoading && !staffTenantId && isOnLoginPage) {
      // Only mark as initialized on login page without tenant
      useAuthStore.setState({
        initialized: true,
        tenantError: null,
      })
    }
  }, [staffTenantId, tenantLoading, tenantError])

  // One-time token refresh for returning demo users.
  // When a demo user returns (e.g. closes browser, comes back later), their cached
  // token may have stale custom claims (demoCorporateId: 'pending'). A force refresh
  // updates the token with current claims; onIdTokenChanged then propagates it.
  // This runs once per uid when the user is first authenticated.
  useEffect(() => {
    if (!user) return
    if (!getIsDemoContext()) return

    // Skip demo-managed routes that handle their own auth/token state
    // (/creating, /verify, /signup, /magic-link, /reset-password and their subpaths).
    const DEMO_MANAGED_PATHS = ['/creating', '/verify', '/signup', '/magic-link', '/reset-password']
    const isOnManagedPath = DEMO_MANAGED_PATHS.some(
      (p) => window.location.pathname === p || window.location.pathname.startsWith(p + '/')
    )
    if (isOnManagedPath) return

    // Force refresh once to get fresh custom claims
    user.getIdToken(true).catch(() => {
      // Silently fail — onIdTokenChanged will fire with the best available token
    })
  }, [user?.uid]) // Only re-run when user uid changes (i.e. first sign-in per user)

  // Token refresh safety net (backup for edge cases)
  // Firebase auto-refreshes tokens at ~55min, but can fail during:
  // - Browser sleep/hibernation (laptop closed for hours)
  // - Network interruptions (train/plane connectivity issues)
  // - Tab backgrounding (Chrome throttles background timers)
  // - Memory pressure (mobile browser suspends JS execution)
  // - Clock skew (system time changes, timezone travel)
  // This 50min interval ensures we always have fresh tokens even if Firebase auto-refresh fails
  useEffect(() => {
    if (!user) {
      return
    }

    const interval = setInterval(
      async () => {
        try {
          if (auth.currentUser) {
            await auth.currentUser.getIdToken(true) // Force refresh with server time
          }
        } catch (_) {
          // Token refresh failed
        }
      },
      50 * 60 * 1000
    ) // Every 50 minutes (10min safety margin before 60min expiry)

    return () => clearInterval(interval)
  }, [user])

  const isOnLoginPage =
    typeof window !== 'undefined' &&
    (window.location.pathname === '/login' || window.location.pathname.includes('/login'))

  const shouldShowLoading = isOnLoginPage ? false : authLoading || (!!user && tenantLoading)

  // Brand not found: tenant config fetch failed and no usable tenant was resolved.
  // This covers CORS errors, 404s, and any network failure for an unknown subdomain.
  const isBrandNotFound = !!tenantError && !staffTenantId && !tenantLoading

  return {
    isLoading: shouldShowLoading,
    error: isOnLoginPage ? null : tenantError,
    isBrandNotFound,
  }
}
